import React from "react";
import {
  DndContext,
  closestCenter,
  useSensor,
  useSensors,
  PointerSensor,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  Box,
  Collapse,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Paper,
  IconButton,
  alpha,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { arrayToExpression } from "../utils/TextField/NumericTextField";
import { formatAmount } from "../../utils/hooks/dataHandler";
import { useTheme } from "@emotion/react";
import { headerStyle } from "../MainTaxComputation/utils/DetailsTable";
import { getDetailPlTableDisplayConfig } from "../../utils/responsiveLayoutSettings";

// Generic Draggable Row
function DraggableRow({
  id,
  theme,
  children,
  canReorder = true,
  displayConfig,
}) {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id, disabled: !canReorder });
  const dragColumnWidth = displayConfig.dragColumnWidth;

  return (
    <TableRow
      ref={setNodeRef}
      data-detail-pl-row-id={id}
      sx={{
        transform: CSS.Transform.toString(transform),
        transition,
        "&:hover": {
          bgcolor: alpha(theme.palette.primary.main, 0.04),
        },
      }}
    >
      {canReorder && (
        <TableCell
          {...attributes}
          {...listeners}
          sx={{
            cursor: "grab",
            height: 44,
            width: dragColumnWidth,
            minWidth: dragColumnWidth,
            maxWidth: dragColumnWidth,
            p: 0,
            textAlign: "center",
            userSelect: "none",
            verticalAlign: "middle",
          }}
        >
          ⠿
        </TableCell>
      )}
      {children}
    </TableRow>
  );
}

// Reusable Table Component
export default function ReorderableTable({
  rows,
  section,
  expandedRows,
  setRows,
  columns,
  rowKey = "id",
  children,
  onToggleRowExpansion,
  canReorder = true,
  maxHeight = "min(560px, calc(100vh - 260px))",
  tableDisplayConfig,
}) {
  const theme = useTheme();
  const sensors = useSensors(useSensor(PointerSensor));
  const displayConfig =
    tableDisplayConfig || getDetailPlTableDisplayConfig(false);

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!active?.id || !over?.id || active.id === over.id) return;

    setRows((prev) => {
      const oldIndex = rows.findIndex((r) => r[rowKey] === active.id);
      const newIndex = rows.findIndex((r) => r[rowKey] === over.id);
      if (oldIndex === -1 || newIndex === -1) return prev;

      const reordered = arrayMove(rows, oldIndex, newIndex);

      return reordered;
    });
  };

  const tableContent = (
    <TableContainer
      component={Paper}
      sx={{
        maxHeight,
        overflow: "auto",
        borderRadius: 0,
        boxShadow: "none",
        "& .MuiTable-root": {
          minWidth: displayConfig.tableMinWidth,
        },
      }}
    >
      <Table stickyHeader>
        <TableHead>
          <TableRow>
            {canReorder && (
              <TableCell
                sx={{
                  ...headerStyle(theme),
                  width: displayConfig.dragColumnWidth,
                  minWidth: displayConfig.dragColumnWidth,
                  maxWidth: displayConfig.dragColumnWidth,
                  p: 0,
                }}
              />
            )}
            {columns.map((col) => (
              <TableCell
                key={col.key}
                align={col.headerAlign || "center"}
                sx={{
                  ...headerStyle(theme),
                  width: col.width || "auto",
                  minWidth: col.width || "auto",
                  maxWidth: col.width || "auto",
                  ...(col.fixedSize ? { p: 0 } : {}),
                }}
              >
                {col.label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, index) => {
            const isExpanded = expandedRows?.[`${section}-${index}`];

            return (
              <React.Fragment key={"content_" + row[rowKey]}>
                <DraggableRow
                  id={row[rowKey]}
                  theme={theme}
                  canReorder={canReorder}
                  displayConfig={displayConfig}
                >
                  {columns.map((col) => (
                    <TableCell
                      key={col.key}
                      align={col.colAlign || "center"}
                      sx={{
                        width: col.width || "auto",
                        minWidth: col.width || "auto",
                        maxWidth: col.width || "auto",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        borderColor: alpha(theme.palette.divider, 0.1),
                        py: col.fixedSize ? 0 : displayConfig.rowPaddingY,
                        ...(col.fixedSize
                          ? {
                              height: 44,
                              p: 0,
                              verticalAlign: "middle",
                            }
                          : {}),
                      }}
                    >
                      {col.render ? col.render(row, index) : row[col.key]}
                    </TableCell>
                  ))}
                </DraggableRow>

                {/* Collapsible Row */}
                <TableRow>
                  <TableCell
                    colSpan={columns.length + (canReorder ? 1 : 0)}
                    sx={{ p: 0 }}
                  >
                    <Collapse in={isExpanded} timeout='auto' unmountOnExit>
                      <Box
                        sx={{
                          margin: 2,
                          backgroundColor: "background.paper",
                          borderRadius: 3,
                          p: 3,
                          boxShadow: "0 4px 12px rgba(96, 63, 151, 0.08)",
                          border: "1px solid",
                          borderColor: "action.hover",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            mb: 2,
                            pb: 2,
                            borderBottom: "2px solid",
                            borderColor: "action.hover",
                          }}
                        >
                          <Typography
                            variant='h6'
                            component='div'
                            sx={{
                              color: "text.primary",
                              fontWeight: 600,
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            Merged Row Details
                          </Typography>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 2,
                            }}
                          >
                            {row.history?.length > 0 && (
                              <Typography
                                variant='caption'
                                sx={{
                                  backgroundColor: "primary.main",
                                  color: "primary.contrastText",
                                  padding: "4px 12px",
                                  borderRadius: "16px",
                                  fontWeight: 600,
                                  fontSize: "0.75rem",
                                }}
                              >
                                {row.history.length} row
                                {row.history.length > 1 ? "s" : ""} merged
                              </Typography>
                            )}
                            <IconButton
                              size='small'
                              onClick={() => {
                                if (onToggleRowExpansion) {
                                  onToggleRowExpansion(section, index);
                                }
                              }}
                              sx={{
                                color: "text.secondary",
                                "&:hover": {
                                  backgroundColor: "action.hover",
                                  color: "text.primary",
                                },
                              }}
                            >
                              <CloseIcon fontSize='small' />
                            </IconButton>
                          </Box>
                        </Box>

                        {!row.history?.length ? (
                          <Box
                            sx={{
                              p: 4,
                              textAlign: "center",
                              color: "text.secondary",
                              backgroundColor: "action.hover",
                              borderRadius: 2,
                              border: "1px dashed",
                              borderColor: "text.disabled",
                            }}
                          >
                            <Typography variant='body2'>
                              No merged rows for this entry
                            </Typography>
                          </Box>
                        ) : (
                          <Table
                            size='small'
                            sx={{
                              backgroundColor: "background.paper",
                              borderRadius: 2,
                              overflow: "hidden",
                            }}
                          >
                            <TableHead>
                              <TableRow
                                sx={{ backgroundColor: "action.hover" }}
                              >
                                <TableCell
                                  align='left'
                                  sx={{
                                    fontWeight: 600,
                                    color: "text.primary",
                                    borderBottom: "2px solid",
                                    borderColor: "text.disabled",
                                  }}
                                >
                                  Row Name
                                </TableCell>
                                <TableCell
                                  align='right'
                                  sx={{
                                    fontWeight: 600,
                                    color: "text.primary",
                                    borderBottom: "2px solid",
                                    borderColor: "text.disabled",
                                  }}
                                >
                                  Amount
                                </TableCell>
                                <TableCell
                                  align='center'
                                  sx={{
                                    fontWeight: 600,
                                    color: "text.primary",
                                    borderBottom: "2px solid",
                                    borderColor: "text.disabled",
                                  }}
                                >
                                  Row Reference
                                </TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {row.history?.map((h, hIndex) => (
                                <TableRow
                                  key={hIndex}
                                  sx={{
                                    "&:hover": {
                                      backgroundColor: "action.hover",
                                    },
                                    "&:nth-of-type(even)": {
                                      backgroundColor: "action.selected",
                                    },
                                    "&:nth-of-type(even):hover": {
                                      backgroundColor: "action.focus",
                                    },
                                  }}
                                >
                                  <TableCell
                                    variant='body2'
                                    sx={{
                                      color: "text.primary",
                                      fontWeight: 500,
                                    }}
                                  >
                                    {h["name"]}
                                  </TableCell>
                                  <TableCell
                                    variant='body2'
                                    align='right'
                                    sx={{
                                      color: "text.primary",
                                      fontFamily: "monospace",
                                    }}
                                  >
                                    <Box>
                                      <Typography
                                        variant='body2'
                                        sx={{
                                          fontWeight: 600,
                                          color: "text.primary",
                                        }}
                                      >
                                        {formatAmount(
                                          []
                                            .concat(h.amount || [])
                                            .reduce((sum, num) => sum + num, 0)
                                        )}
                                      </Typography>
                                      {h["amount"] &&
                                        h["amount"].length > 1 && (
                                          <Typography
                                            variant='caption'
                                            sx={{
                                              color: "text.secondary",
                                              display: "block",
                                            }}
                                          >
                                            ={" "}
                                            {arrayToExpression(
                                              h["amount"] || []
                                            )}
                                          </Typography>
                                        )}
                                    </Box>
                                  </TableCell>
                                  <TableCell variant='body2' align='center'>
                                    <Typography
                                      variant='caption'
                                      sx={
                                        !h["reference"] && {
                                          backgroundColor: "primary.main",
                                          color: "white",
                                          padding: "4px 8px",
                                          borderRadius: "12px",
                                          fontFamily: "monospace",
                                          fontSize: "0.75rem",
                                          fontWeight: 500,
                                        }
                                      }
                                    >
                                      {h["reference"] || "N/A"}
                                    </Typography>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        )}
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            );
          })}
          {children}
        </TableBody>
      </Table>
    </TableContainer>
  );

  if (!canReorder) {
    return tableContent;
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext
        items={rows.map((row) => row[rowKey])}
        strategy={verticalListSortingStrategy}
      >
        {tableContent}
      </SortableContext>
    </DndContext>
  );
}
